import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useIsMobile } from '../../../../hooks/useIsMobile';
import type { DateRange } from '@repo/ui/components/Calendar';
import type { BaseAnalyticsFilter } from '@repo/shared/dtos/analytics/AnalyticsFilterDto';
import { Calendar } from '@repo/ui/components/Calendar';
import type { DayClickEventHandler } from 'react-day-picker';
import { ANALYTICS_CONSTANTS } from '../../constants';
import { usePanelPosition } from '../hooks/usePanelPosition';
import {
  formatUtcDate,
  getDateWithOffset,
  getInclusiveDayCount,
  isWithinDailyLimit,
  toCalendarDate,
  toUtcEndOfDay,
  toUtcStartOfDay,
} from '../utils/dateRangeUtils';

type RangeBoundary = 'start' | 'end';

const buttonStyles = 'rounded-md border px-3 py-1.5 text-sm transition-colors';
const quickButtonStyles =
  'rounded border border-white/20 px-2 py-1.5 text-xs text-slate-200 transition-colors cursor-pointer';
const boundaryToggleButtonStyles = 'px-2 py-1 text-xs font-medium transition-colors';
const RANGE_BOUNDARY_LABEL: Readonly<Record<RangeBoundary, string>> = {
  start: 'Start',
  end: 'End',
};

interface DayRangeControlsProps {
  readonly filters: BaseAnalyticsFilter;
  readonly onChange: (patch: Partial<BaseAnalyticsFilter>) => void;
  readonly showQuickPresets?: boolean;
}
export function DayRangeControls({ filters, onChange, showQuickPresets }: DayRangeControlsProps) {
  const isMobile = useIsMobile();
  const [showRangePicker, setShowRangePicker] = useState(false);
  const rangeRef = useRef<HTMLDivElement>(null);
  const panelPos = usePanelPosition(rangeRef, showRangePicker, { maxWidth: 360, panelHeight: 420 });
  const { startDate, endDate } = filters;
  const hasRange = Boolean(startDate && endDate);
  const [activeBoundary, setActiveBoundary] = useState<RangeBoundary>(() =>
    !startDate ? 'start' : 'end',
  );

  const dayRange: DateRange = useMemo(
    () => ({ from: toCalendarDate(filters.startDate), to: toCalendarDate(filters.endDate) }),
    [filters.startDate, filters.endDate],
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (rangeRef.current && !rangeRef.current.contains(event.target as Node)) {
        setShowRangePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!startDate && endDate) {
      setActiveBoundary('start');
      return;
    }
    if (startDate && !endDate) {
      setActiveBoundary('end');
      return;
    }
    if (!startDate && !endDate) {
      setActiveBoundary('start');
    }
  }, [startDate, endDate]);

  const commitRange = (nextStart: Date, nextEnd: Date) => {
    if (!isWithinDailyLimit(nextStart, nextEnd)) {
      return;
    }

    onChange({ startDate: nextStart, endDate: nextEnd, granularity: 'day' });
  };

  const handleBoundarySelection = (date: Date) => {
    const normalizedStart = toUtcStartOfDay(date);
    const normalizedEnd = toUtcEndOfDay(date);

    if (!startDate && !endDate) {
      commitRange(normalizedStart, normalizedEnd);
      return;
    }

    if (activeBoundary === 'start') {
      const currentEnd = endDate ? toUtcEndOfDay(endDate) : normalizedEnd;
      const safeEnd =
        normalizedStart.getTime() <= currentEnd.getTime() ? currentEnd : normalizedEnd;
      commitRange(normalizedStart, safeEnd);
      return;
    }

    const currentStart = startDate ? toUtcStartOfDay(startDate) : normalizedStart;
    const safeStart =
      currentStart.getTime() <= normalizedEnd.getTime() ? currentStart : normalizedStart;
    commitRange(safeStart, normalizedEnd);
  };

  const handleDayClick: DayClickEventHandler = (date, modifiers) => {
    if (modifiers.disabled) {
      return;
    }

    handleBoundarySelection(date);
  };

  const formatRangeLabel = (): string => {
    if (startDate && endDate) {
      return `${formatUtcDate(startDate)} - ${formatUtcDate(endDate)}`;
    }
    if (startDate) {
      return `${formatUtcDate(startDate)} - Select end`;
    }
    if (endDate) {
      return `Select start - ${formatUtcDate(endDate)}`;
    }
    return 'Select range';
  };

  const panelClass =
    panelPos.type === 'fixed'
      ? 'fixed z-50 rounded-lg border border-[var(--anl-border)] bg-[var(--anl-surface)] p-3 text-[var(--anl-text)] shadow-lg'
      : 'absolute left-0 top-full z-50 mt-1 rounded-lg border border-[var(--anl-border)] bg-[var(--anl-surface)] p-3 text-[var(--anl-text)] shadow-lg';

  const panelStyle =
    panelPos.type === 'fixed'
      ? { left: panelPos.left, top: panelPos.top, width: panelPos.width }
      : { minWidth: '540px' };

  return (
    <div className="flex items-center gap-2 sm:gap-3">
      <div className="relative" ref={rangeRef}>
        <button
          onClick={() => setShowRangePicker(prev => !prev)}
          className={`${buttonStyles} ${hasRange ? 'text-white' : 'border-white/20 text-slate-300 hover:bg-[var(--anl-surface-elev)]'}`}
          style={
            hasRange
              ? { backgroundColor: 'var(--anl-warning)', borderColor: 'var(--anl-warning)' }
              : { backgroundColor: '#454b4d' }
          }
        >
          <span className="flex items-center space-x-1">
            <span className="font-medium">{formatRangeLabel()}</span>
            <ChevronDown
              size={12}
              className={`flex-shrink-0 transition-transform ${showRangePicker ? 'rotate-180' : ''}`}
            />
          </span>
        </button>

        {showRangePicker && (
          <div className={panelClass} style={panelStyle}>
            <Calendar
              mode="range"
              selected={dayRange}
              onDayClick={handleDayClick}
              numberOfMonths={isMobile ? 1 : 2}
              className="rounded-md border border-[var(--anl-border)] bg-[var(--anl-surface-elev)] p-3 text-[var(--anl-text)]"
              defaultMonth={toCalendarDate(filters.startDate) || new Date()}
              fromYear={ANALYTICS_CONSTANTS.EARLIEST_DATA_YEAR}
              toYear={new Date().getFullYear()}
              disabled={date => {
                if (!date || !(date instanceof Date)) return false;
                return date.getFullYear() < ANALYTICS_CONSTANTS.EARLIEST_DATA_YEAR;
              }}
            />

            {startDate && endDate && (
              <div className="mt-3 flex items-center justify-between text-xs text-slate-300">
                <span>{getInclusiveDayCount(startDate, endDate)} days selected</span>
                <div className="flex items-center gap-2">
                  <div className="inline-flex overflow-hidden rounded-md border border-[var(--anl-border)]">
                    {(['start', 'end'] as RangeBoundary[]).map((boundary, index) => (
                      <button
                        key={boundary}
                        type="button"
                        className={`${boundaryToggleButtonStyles} ${
                          activeBoundary === boundary
                            ? 'bg-[var(--anl-accent)] text-white'
                            : 'bg-[var(--anl-surface-elev)] text-[var(--anl-text)]'
                        } ${index === 0 ? '' : 'border-l border-[var(--anl-border)]'}`}
                        onClick={() => setActiveBoundary(boundary)}
                        aria-pressed={activeBoundary === boundary}
                      >
                        {RANGE_BOUNDARY_LABEL[boundary]}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {showQuickPresets && (
        <div className="relative z-10 flex items-center space-x-1">
          <button
            onClick={() => {
              const now = new Date();
              const end = toUtcEndOfDay(now);
              const start = toUtcStartOfDay(getDateWithOffset(now, -6));
              onChange({ startDate: start, endDate: end, granularity: 'day' });
            }}
            className={quickButtonStyles}
            style={{ backgroundColor: '#454b4d' }}
          >
            1W
          </button>
          <button
            onClick={() => {
              const now = new Date();
              const end = toUtcEndOfDay(now);
              const start = toUtcStartOfDay(getDateWithOffset(now, -29));
              onChange({ startDate: start, endDate: end, granularity: 'day' });
            }}
            className={quickButtonStyles}
            style={{ backgroundColor: '#454b4d' }}
          >
            1M
          </button>
          <button
            onClick={() => {
              const now = new Date();
              const end = toUtcEndOfDay(now);
              const start = toUtcStartOfDay(getDateWithOffset(now, -89));
              onChange({ startDate: start, endDate: end, granularity: 'day' });
            }}
            className={quickButtonStyles}
            style={{ backgroundColor: '#454b4d' }}
          >
            3M
          </button>
        </div>
      )}
    </div>
  );
}

DayRangeControls.displayName = 'DayRangeControls';
